import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import * as Sentry from "@sentry/react";
import App from "./App";
import { initAuth } from "./lib/auth";
import { emitUnauthorized } from "./lib/unauthorized";
import "./index.css";

// Sentry — only active in production when VITE_SENTRY_DSN is set.
if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn:              import.meta.env.VITE_SENTRY_DSN,
    environment:      import.meta.env.MODE,
    release:          import.meta.env.VITE_SENTRY_RELEASE,
    tracesSampleRate: 0.1,
    // Capture unhandled promise rejections and JS errors automatically
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
  })
}

// Restore the session from localStorage before mounting the app
initAuth();

function handle401(error: unknown) {
  if (error instanceof Error && (error.cause as Response | undefined)?.status === 401) {
    emitUnauthorized();
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handle401 }),
  mutationCache: new MutationCache({ onError: handle401 }),
  defaultOptions: {
    // refetchInterval is NOT set globally — only queries that need live polling
    // declare it explicitly. Static reference data (clients, parties, products,
    // etc.) must NOT poll so that presigned S3 URLs don't regenerate on every
    // cycle and cause avatar/image flickering.
    queries: { staleTime: 4_000 },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please reload the page.</p>}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
